import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { NotificationType } from 'angular2-notifications'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { LogService } from '../../services/log.service'
import { NotificationService } from '../../services/notification.service'
import { UtilityService } from '../../services/utility.service'
import { AppState } from '../app.state'
import * as ForFundingActions from './for-funding.actions'
import { selectForFundingSearchOpen } from './for-funding.selectors'
import { ForFundingService } from './for-funding.service'

@Injectable()
export class ForFundingEffects {
   private _cn = this.constructor.name
   private _router = inject(Router)
   private _actions$ = inject(Actions)
   private _logService = inject(LogService)
   private _store = inject(Store<AppState>)
   private _forFundingService = inject(ForFundingService)
   private _utilityService = inject(UtilityService)
   private _notificationService = inject(NotificationService)

   toggleLinkingForFundingSearch$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.toggleForFundingSearch),
         withLatestFrom(this._store.pipe(select(selectForFundingSearchOpen))),
         switchMap(([action, isSearchOpen]) => {
            return of(isSearchOpen ? ForFundingActions.closeForFundingSearch() : ForFundingActions.openForFundingSearch())
         })
      )
   )

   searchForFundingData$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.searchForFundingData),
         switchMap((action) =>
            this._forFundingService.searchForFundingData(action.searchFields).pipe(
               tap((res) => this._logService.debug(this._cn, 'searchForFundingsData effect', 'response:', res)),
               map((ForFundingsItems) => ForFundingActions.searchForFundingDataSuccess({ data: ForFundingsItems })),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.searchForFundingDataFailure({ error: error }))
               })
            )
         )
      )
   )

   downloadhForFundingData$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.downloadForFundingData),
         switchMap((action) =>
            this._forFundingService.downloadForFundingData(action.searchFields).pipe(
               tap((res) => this._logService.debug(this._cn, 'download ForFundingsData effect', 'response:', res)),
               map((response) => {
                  if (response.body) {
                     this._utilityService.downloadFile(response)
                     return ForFundingActions.downloadForFundingDataSuccess()
                  } else {
                     return ForFundingActions.downloadForFundingDataFailure({
                        error: new HttpErrorResponse({ error: '0 - No blob in response to download' }),
                     })
                  }
               }),

               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.downloadForFundingDataFailure({ error: error }))
               })
            )
         )
      )
   )

   addForFundingComment$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.addForFundingComment),
         switchMap((action) =>
            this._forFundingService.addForFundingComment(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'addForFundingsComment effect', 'response:', res)),
               map(() => ForFundingActions.addForFundingCommentSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.addForFundingCommentFailure({ error: error }))
               })
            )
         )
      )
   )

   deleteForFundingComment$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.deleteForFundingComment),
         switchMap((action) =>
            this._forFundingService.deleteForFundingComment(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'deleteForFundingsComment effect', 'response:', res)),
               map(() => ForFundingActions.deleteForFundingCommentSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.deleteForFundingCommentFailure({ error: error }))
               })
            )
         )
      )
   )

   addForFundingCoordinator$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.addForFundingCoordinator),
         switchMap((action) =>
            this._forFundingService.addForFundingCoordinator(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'addForFundingsCoordinator effect', 'response:', res)),
               map((res) => ForFundingActions.addForFundingCoordinatorSuccess({ res })),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.addForFundingCoordinatorFailure({ error: error }))
               })
            )
         )
      )
   )

   editForFundingCoordinator$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.editForFundingCoordinator),
         switchMap((action) =>
            this._forFundingService.editForFundingCoordinator(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'editForFundingsCoordinator effect', 'response:', res)),
               map((res) => ForFundingActions.editForFundingCoordinatorSuccess({ res })),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.editForFundingCoordinatorFailure({ error: error }))
               })
            )
         )
      )
   )

   deleteForFundingCoordinator$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.deleteForFundingCoordinator),
         switchMap((action) =>
            this._forFundingService.deleteForFundingCoordinator(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'deleteForFundingsCoordinator effect', 'response:', res)),
               map(() => ForFundingActions.deleteForFundingCoordinatorSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.deleteForFundingCoordinatorFailure({ error: error }))
               })
            )
         )
      )
   )

   addForFundingPartner$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.addForFundingPartner),
         switchMap((action) =>
            this._forFundingService.addForFundingPartner(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'addForFundingsPartner effect', 'response:', res)),
               map((res) => ForFundingActions.addForFundingPartnerSuccess({ res })),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.addForFundingPartnerFailure({ error: error }))
               })
            )
         )
      )
   )

   editForFundingPartner$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.editForFundingPartner),
         switchMap((action) =>
            this._forFundingService.editForFundingPartner(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'editForFundingsPartner effect', 'response:', res)),
               map((res) => ForFundingActions.editForFundingPartnerSuccess({ res })),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.editForFundingPartnerFailure({ error: error }))
               })
            )
         )
      )
   )

   deleteForFundingPartner$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.deleteForFundingPartner),
         switchMap((action) =>
            this._forFundingService.deleteForFundingPartner(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'deleteForFundingsPartner effect', 'response:', res)),
               map(() => ForFundingActions.deleteForFundingPartnerSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.deleteForFundingPartnerFailure({ error: error }))
               })
            )
         )
      )
   )

   addForFundingResponsible$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.addForFundingResponsible),
         switchMap((action) =>
            this._forFundingService.addForFundingResponsible(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'addForFundingsResponsible effect', 'response:', res)),
               map(() => ForFundingActions.addForFundingResponsibleSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.addForFundingResponsibleFailure({ error: error }))
               })
            )
         )
      )
   )

   addForFundingPNOResponsible$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.addForFundingPNOResponsible),
         switchMap((action) =>
            this._forFundingService.addForFundingPNOResponsible(action.matchmakingId).pipe(
               tap((res) => this._logService.debug(this._cn, 'addForFundingsPNOResponsible effect', 'response:', res)),
               map(() => ForFundingActions.addForFundingPNOResponsibleSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.addForFundingPNOResponsibleFailure({ error: error }))
               })
            )
         )
      )
   )

   deleteForFundingResponsible$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.deleteForFundingResponsible),
         switchMap((action) =>
            this._forFundingService.deleteForFundingResponsible(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'deleteForFundingsResponsible effect', 'response:', res)),
               map(() => ForFundingActions.deleteForFundingResponsibleSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.deleteForFundingResponsibleFailure({ error: error }))
               })
            )
         )
      )
   )

   contactForFundingResponsible$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.contactForFundingResponsible),
         switchMap((action) =>
            this._forFundingService.contactForFundingResponsible(action.grantId, action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'contactForFundingsResponsible effect', 'response:', res)),
               map(() => {
                  return ForFundingActions.contactForFundingResponsibleSuccess()
               }),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.contactForFundingResponsibleFailure({ error: error }))
               })
            )
         )
      )
   )

   getFundingDataByID$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.getFundingDataByID),
         switchMap((action) =>
            this._forFundingService.getFundingDataByID(action.id).pipe(
               tap((res) => this._logService.debug(this._cn, 'getFundingDataByID effect', 'response:', res)),
               map((ForFundingsDetail) => ForFundingActions.getFundingDataByIDSuccess({ data: ForFundingsDetail })),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.getFundingDataByIDFailure({ error: error }))
               })
            )
         )
      )
   )

   // seeking partners
   getSoughtPartners$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.getSoughtPartners),
         switchMap((action) =>
            this._forFundingService.getSoughtPartners(action.matchmakingId).pipe(
               tap((res) => this._logService.debug(this._cn, 'getSoughtPartners effect', 'response:', res)),
               map((data) => ForFundingActions.getSoughtPartnersSuccess({ data })),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.getSoughtPartnersFailure({ error: error }))
               })
            )
         )
      )
   )

   saveSoughtPartnerAndSendNotification$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.saveSoughtPartnerAndSendNotification),
         switchMap((action) =>
            this._forFundingService.saveSoughtPartnerAndSendNotification(action.matchmakingId, action.data).pipe(
               map(() => ForFundingActions.saveSoughtPartnerAndSendNotificationSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.saveSoughtPartnerAndSendNotificationFailure({ error: error }))
               })
            )
         )
      )
   )

   sendJustNotificationForSoughtPartner$ = createEffect(() =>
      this._actions$.pipe(
         ofType(ForFundingActions.sendJustNotificationForSoughtPartner),
         switchMap((action) =>
            this._forFundingService.sendJustNotificationForSoughtPartner(action.matchmakingId).pipe(
               map(() => ForFundingActions.sendJustNotificationForSoughtPartnerSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(ForFundingActions.sendJustNotificationForSoughtPartnerFailure({ error: error }))
               })
            )
         )
      )
   )

   /* Notification error message */
   private _actionsForErrorNotificationMessage = [
      ForFundingActions.searchForFundingDataFailure,
      ForFundingActions.downloadForFundingDataFailure,
      ForFundingActions.addForFundingCommentFailure,
      ForFundingActions.deleteForFundingCommentFailure,
      ForFundingActions.addForFundingCoordinatorFailure,
      ForFundingActions.editForFundingCoordinatorFailure,
      ForFundingActions.deleteForFundingCoordinatorFailure,
      ForFundingActions.addForFundingPartnerFailure,
      ForFundingActions.editForFundingPartnerFailure,
      ForFundingActions.deleteForFundingPartnerFailure,
      ForFundingActions.addForFundingResponsibleFailure,
      ForFundingActions.deleteForFundingResponsibleFailure,
      ForFundingActions.addForFundingPNOResponsibleFailure,
      ForFundingActions.contactForFundingResponsibleFailure,
      ForFundingActions.getFundingDataByIDFailure,
      ForFundingActions.getSoughtPartnersFailure,
      ForFundingActions.saveSoughtPartnerAndSendNotificationFailure,
      ForFundingActions.sendJustNotificationForSoughtPartnerFailure,
   ]

   listenAllFailureAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForErrorNotificationMessage),
            map((errorResponse) => {
               this._notificationService.notify(NotificationType.Error, undefined, `${errorResponse.error.status} - ${errorResponse.error.statusText}`)
            })
         ),
      { dispatch: false }
   )

   /* Notification success message */
   private _actionsForSuccessNotificationMessage = [
      ForFundingActions.contactForFundingResponsibleSuccess,
      ForFundingActions.saveSoughtPartnerAndSendNotificationSuccess,
      ForFundingActions.sendJustNotificationForSoughtPartnerSuccess,
   ]

   listenAllSuccessAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForSuccessNotificationMessage),
            map(() => this._notificationService.notify(NotificationType.Success))
         ),
      { dispatch: false }
   )
}
